import { Coins, CheckCircle2, ShieldCheck, ArrowRight, Building2, Star } from 'lucide-react';
import ContactForm from '../../components/ContactForm';
import SEO from '../../components/SEO';

export default function ErhvervsleasingEn() {
  const leasingEksempler = [
    { 
      mdr: '24 months', 
      prMnd: '€187', 
      udbetaling: '€425', 
      restvaerdi: '€425', 
      beskrivelse: 'Rapid depreciation and short commitment',
      popular: false
    },
    { 
      mdr: '36 months', 
      prMnd: '€129', 
      udbetaling: '€425', 
      restvaerdi: '€425', 
      beskrivelse: 'Most popular option for equestrian businesses',
      popular: true
    },
    { 
      mdr: '48 months', 
      prMnd: '€97', 
      udbetaling: '€425', 
      restvaerdi: '€425', 
      beskrivelse: 'Lowest monthly rate, maximized operational cashflow',
      popular: false
    }
  ];

  const trin = [
    { step: '01', title: 'Complete application', desc: 'Send your company information and preferred leasing term.' },
    { step: '02', title: 'Prompt review', desc: 'Application is processed in partnership with Ikano Bank Leasing.' },
    { step: '03', title: 'Production & Delivery', desc: 'Once approved and signed electronically, your machine enters production and ships promptly.' }
  ];

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-950 antialiased">
      <SEO
        title="Commercial Leasing of Danshaker - Ikano Bank"
        description="Commercial equipment leasing for businesses from €97/month. Preserve working capital with attractive B2B leasing in partnership with Ikano Bank."
        path="/en/commercial-leasing"
        lang="en"
      />

      {/* B2B Dark Hero Section */}
      <section className="relative overflow-hidden bg-slate-950 py-20 text-white border-b border-slate-800">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,103,162,0.15),transparent_50%)]" />
        <div className="mx-auto max-w-7xl px-4 md:px-8 relative z-10">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/20 border border-[#0E67A2]/40 px-4 py-1.5 text-xs font-black text-sky-400 uppercase tracking-wider">
              <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Commercial Equipment Leasing</span>
            </div>
            <h1 className="text-4xl font-black tracking-tight sm:text-6xl text-white">
              Commercial Leasing
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 font-medium leading-relaxed max-w-2xl">
              For businesses and equestrian facilities, we offer highly competitive commercial leasing in partnership with <strong className="text-[#0E67A2]">Ikano Bank Leasing</strong>. Protect your operational liquidity with tax-deductible monthly installments.
            </p>
            <div className="pt-2">
              <a
                href="#leasing-formular"
                className="rounded-xl bg-[#0E67A2] px-8 py-4 font-bold text-white shadow-lg transition hover:bg-[#0E67A2]/90 inline-flex items-center gap-2"
              >
                Apply for commercial leasing
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Pricing Models */}
      <section className="py-20 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-black text-slate-900 sm:text-4xl">Leasing Examples</h2>
            <p className="mt-4 text-slate-700 font-semibold">Exemplary calculations based on standard machine price. Excl. VAT.</p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {leasingEksempler.map((eks, i) => (
              <div 
                key={i} 
                className={`rounded-3xl border-2 p-8 relative flex flex-col justify-between ${
                  eks.popular 
                    ? 'border-[#0E67A2] bg-slate-50 shadow-xl' 
                    : 'border-slate-200 bg-white shadow-sm'
                }`}
              >
                {eks.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#0E67A2] px-4 py-1 text-xs font-black text-white uppercase tracking-wider shadow-sm flex items-center gap-1">
                    <Star className="h-3 w-3 fill-white" />
                    Most popular
                  </div>
                )}
                <div>
                  <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">{eks.mdr}</div>
                  <div className="text-4xl font-black text-slate-900 mb-1">{eks.prMnd}</div>
                  <span className="text-xs text-slate-500 font-semibold block mb-6">per month excl. VAT</span>
                  <p className="text-xs text-slate-700 font-bold border-t border-slate-200 pt-4 mb-6">{eks.beskrivelse}</p>

                  <div className="space-y-2 text-xs text-slate-600 font-medium">
                    <div className="flex justify-between border-b border-slate-100 pb-1.5">
                      <span>Down payment (approx. 10%):</span>
                      <strong className="text-slate-900">{eks.udbetaling}</strong>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-1.5">
                      <span>Residual value (approx. 10%):</span>
                      <strong className="text-slate-900">{eks.restvaerdi}</strong>
                    </div>
                  </div>
                </div>

                <div className="pt-8">
                  <a
                    href="#leasing-formular"
                    className={`flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition ${
                      eks.popular
                        ? 'bg-[#0E67A2] text-white hover:bg-[#0E67A2]/90 shadow-md'
                        : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                    }`}
                  >
                    Select this option
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 Steps */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-black text-slate-900 sm:text-4xl">How the leasing process works</h2>
            <p className="mt-4 text-slate-700 font-semibold">Fast, digital, and straightforward</p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {trin.map((t, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                <span className="text-xs font-black text-[#0E67A2] tracking-widest block mb-2">{t.step}</span>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{t.title}</h3>
                <p className="text-sm text-slate-600 font-medium leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="leasing-formular" className="py-24 bg-white scroll-mt-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <div className="text-center space-y-4 mb-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-bold text-[#0E67A2]">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Confidential application</span>
            </div>
            <h2 className="text-3xl font-black text-slate-900 sm:text-4xl">
              Apply for commercial leasing
            </h2>
            <p className="text-slate-600 text-base max-w-xl mx-auto">
              Please enter your company information and upload your latest annual report. We will process your application confidentially.
            </p>
          </div>

          <div className="rounded-3xl border-2 border-slate-200 bg-slate-50 p-6 md:p-10 shadow-xl">
            <ContactForm 
              source="Erhvervsleasing" 
              defaultService="Erhvervsleasing via Ikano Bank"
              buttonText="Submit leasing application"
              isErhvervsleasing={true}
              lang="en"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
